import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors the public contract of GET /api/v1/audit. */
export interface AuditEntry {
  id: string;
  occurred_at: string;
  action: string;
  outcome: 'success' | 'failure';
  actor: { id: string; email: string; display_name: string } | null;
  /**
   * In the platform's audit log only: the tenant a super administrator was
   * working in when this happened (ADR-028).
   */
  tenant?: { id: string; name: string };
  client_ip: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown>;
}

export interface AuditPage {
  items: AuditEntry[];
  next_cursor: string | null;
}

export type Period = '24h' | '7d' | '30d' | '';

export interface AuditFilter {
  action: string;
  outcome: '' | 'success' | 'failure';
  period: Period;
  actor: string;
}

export const auditKey = ['audit'] as const;

const periodMs: Record<Exclude<Period, ''>, number> = {
  '24h': 24 * 3600_000,
  '7d': 7 * 24 * 3600_000,
  '30d': 30 * 24 * 3600_000,
};

export function listAudit(req: AuthedRequest, f: AuditFilter, cursor: string): Promise<AuditPage> {
  const params = new URLSearchParams({ limit: '50' });
  if (f.action) params.set('action', f.action);
  if (f.outcome) params.set('outcome', f.outcome);
  if (f.actor) params.set('actor', f.actor);
  if (f.period) params.set('from', new Date(Date.now() - periodMs[f.period]).toISOString());
  if (cursor) params.set('cursor', cursor);
  return req<AuditPage>('GET', `/api/v1/audit?${params.toString()}`);
}

export const actionLabels: Record<string, string> = {
  'auth.login': 'Login',
  'auth.logout': 'Logout',
  'auth.password_reset_request': 'Pedido de nova senha por e-mail',
  'auth.password_reset': 'Senha redefinida pelo link do e-mail',
  'auth.session.reuse_detected': 'Reuso de sessão detectado',
  'user.create': 'Usuário criado',
  'user.update': 'Usuário alterado',
  'user.password_change': 'Troca de senha',
  'user.invite_send': 'Convite enviado',
  'user.invite_code': 'Código de confirmação enviado',
  'user.invite_accept': 'Convite aceito',
  'tenant.bootstrap': 'Organização criada',
  'tenant.update': 'Organização alterada',
  'event.acknowledge': 'Evento reconhecido',
  'user.access_update': 'Câmeras do usuário alteradas',
  'live.view': 'Ao vivo assistido',
  'role.create': 'Perfil criado',
  'role.update': 'Perfil alterado',
  'role.delete': 'Perfil excluído',
  'client.create': 'Cliente criado',
  'client.update': 'Cliente alterado',
  'client.delete': 'Cliente excluído',
  'site.create': 'Conta criada',
  'site.update': 'Conta alterada',
  'site.delete': 'Conta excluída',
  'device.create': 'Dispositivo cadastrado',
  'device.update': 'Dispositivo alterado',
  'device.delete': 'Dispositivo excluído',
  'device.probe': 'Teste de conexão',
  'device.channels_probe': 'Teste dos canais',
  'device.channels_discover': 'Canais redescobertos',
  'device.push_key_rotate': 'Nova chave RTMP',
  'device.password_view': 'Senha do dispositivo vista',
  'layout.create': 'Mosaico criado',
  'layout.update': 'Mosaico alterado',
  'layout.delete': 'Mosaico excluído',
  'recording.policy_update': 'Gravação configurada',
  'recording.playback': 'Gravação assistida',
  'recording.hold_create': 'Evidência protegida',
  'recording.hold_release': 'Evidência liberada',
  'recording.export_create': 'Exportação pedida',
  'recording.export_download': 'Exportação baixada',
  'recording.segment_dropped': 'Trecho contínuo apagado na máquina de vídeo (disco cheio)',
  'integration.create': 'Integração criada',
  'integration.update': 'Integração alterada',
  'integration.delete': 'Integração excluída',
  'integration.revoke': 'Integração revogada',
  'integration.password_reset': 'Nova senha da integração',
  'integration.password_view': 'Senha da integração exibida',
  'integration.play': 'Vídeo entregue a integração',
  'platform.tenant_create': 'Empresa criada',
  'platform.tenant_status': 'Situação da empresa alterada',
  'platform.tenant_slug': 'Identificador da empresa alterado',
  'platform.tenant_enter': 'Entrou na empresa',
  'platform.tenant_leave': 'Voltou à plataforma',
  'platform.media_node_create': 'Máquina de vídeo cadastrada',
  'platform.media_node_update': 'Máquina de vídeo alterada',
  'platform.media_node_key': 'Chave da máquina de vídeo trocada',
  'platform.media_node_move': 'Equipamento movido de máquina',
};

export const reasonLabels: Record<string, string> = {
  wrong_password: 'Senha incorreta',
  unknown_user: 'Usuário inexistente',
  unknown_tenant: 'Organização inexistente',
  user_disabled: 'Usuário desativado',
  tenant_suspended: 'Organização suspensa',
  malformed_hash: 'Credencial armazenada inválida',
  rate_limited: 'Pedidos demais para esta conta',
  invalid_token: 'Link inválido, usado ou vencido',
  invite_pending: 'Convite ainda não aceito',
  wrong_code: 'Código incorreto',
  code_expired: 'Código vencido ou com tentativas demais',
  stand_in_user: 'Usuário de Super administrador (não faz login)',
  wrong_current_password: 'Senha atual incorreta',
  manual: 'Manual',
  failover: 'Troca automática',
  rebalance: 'Rebalanceamento',
};

/**
 * An entry with no actor done by the platform itself: the failover moves
 * equipment on its own (ADR-050).
 */
export const bySystem = (e: AuditEntry) => e.actor === null && e.metadata.reason === 'failover';

export const metadataLabels: Record<string, string> = {
  reason: 'Motivo',
  email: 'E-mail',
  tenant_slug: 'Organização',
  session_id: 'Sessão',
  user_id: 'Usuário afetado',
  changes: 'Campos alterados',
  roles: 'Papéis',
  roles_before: 'Papéis (antes)',
  roles_after: 'Papéis (depois)',
  status: 'Novo status',
  name_before: 'Nome (antes)',
  name_after: 'Nome (depois)',
  timezone_before: 'Fuso (antes)',
  timezone_after: 'Fuso (depois)',
  event_id: 'Evento',
  kind: 'Tipo de evento',
  note: 'Observação',
  tenant_id: 'Empresa (ID)',
  tenant_name: 'Empresa',
  admin_email: 'E-mail do administrador',
  slug_before: 'Identificador (antes)',
  slug_after: 'Identificador (depois)',
  status_before: 'Situação (antes)',
  status_after: 'Situação (depois)',
};
